import { useEffect, useCallback, useRef } from 'react';

/**
 * Web Vitals metric types
 */
export interface WebVitalMetric {
  /** Metric name */
  name: 'FCP' | 'LCP' | 'CLS' | 'FID' | 'TTFB' | 'INP';
  /** Metric value */
  value: number;
  /** Rating based on thresholds */
  rating: 'good' | 'needs-improvement' | 'poor';
  /** Delta from previous value (for CLS) */
  delta?: number;
  /** Unique ID for the metric */
  id: string;
  /** Navigation type */
  navigationType?: string;
}

/**
 * Callback function for reporting metrics
 */
export type WebVitalReportCallback = (metric: WebVitalMetric) => void;

/**
 * Options for useWebVitals hook
 */
export interface UseWebVitalsOptions {
  /** Whether to enable metric collection (default: true) */
  enabled?: boolean;
  /** Custom callback for reporting metrics */
  onReport?: WebVitalReportCallback;
  /** Whether to log to console in development (default: true in dev) */
  logToConsole?: boolean;
}

/**
 * Return type for useWebVitals hook
 */
export interface UseWebVitalsReturn {
  /** Collected metrics */
  metrics: Map<string, WebVitalMetric>;
  /** Manually trigger metric collection */
  collectMetrics: () => void;
}

/**
 * Thresholds for Web Vitals ratings
 * Based on: https://web.dev/vitals/
 */
const THRESHOLDS = {
  FCP: { good: 1800, poor: 3000 },
  LCP: { good: 2500, poor: 4000 },
  CLS: { good: 0.1, poor: 0.25 },
  FID: { good: 100, poor: 300 },
  TTFB: { good: 800, poor: 1800 },
  INP: { good: 200, poor: 500 },
};

/**
 * Get rating for a metric value based on thresholds
 */
function getRating(
  name: keyof typeof THRESHOLDS,
  value: number
): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name];

  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Generate unique ID for metric
 */
function generateId(): string {
  return `v${String(Date.now())}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Hook for collecting Web Vitals performance metrics.
 *
 * Collects the following metrics using the Performance API:
 * - FCP (First Contentful Paint): When first content is painted
 * - LCP (Largest Contentful Paint): When largest content is painted
 * - CLS (Cumulative Layout Shift): Visual stability measure
 * - TTFB (Time to First Byte): Server response time
 *
 * Uses PerformanceObserver API for accurate measurements.
 * Falls back gracefully when APIs are not available.
 *
 * @example
 * // Basic usage - logs to console in development
 * function App() {
 *   useWebVitals();
 *   return <div>...</div>;
 * }
 *
 * @example
 * // With custom callback for analytics
 * function App() {
 *   useWebVitals({
 *     onReport: (metric) => {
 *       analytics.track('web-vital', metric);
 *     }
 *   });
 *   return <div>...</div>;
 * }
 *
 * @example
 * // Disable in production
 * function App() {
 *   useWebVitals({
 *     enabled: process.env.NODE_ENV === 'development'
 *   });
 *   return <div>...</div>;
 * }
 */
export function useWebVitals(options: UseWebVitalsOptions = {}): UseWebVitalsReturn {
  const { enabled = true, onReport, logToConsole = import.meta.env.DEV } = options;

  const metricsRef = useRef<Map<string, WebVitalMetric>>(new Map());
  const observersRef = useRef<PerformanceObserver[]>([]);
  const clsValueRef = useRef(0);

  const reportMetric = useCallback(
    (metric: WebVitalMetric) => {
      metricsRef.current.set(metric.name, metric);

      if (logToConsole) {
        const color =
          metric.rating === 'good'
            ? '#00ff9f'
            : metric.rating === 'needs-improvement'
              ? '#ffaa00'
              : '#ff4466';

        console.log(
          `%c[Web Vitals] ${metric.name}: ${metric.value.toFixed(2)}ms (${metric.rating})`,
          `color: ${color}; font-weight: bold;`
        );
      }

      onReport?.(metric);
    },
    [logToConsole, onReport]
  );

  const collectMetrics = useCallback(() => {
    // Collect TTFB from navigation timing
    const navigation = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (navigation) {
      const ttfb = navigation.responseStart - navigation.requestStart;
      reportMetric({
        name: 'TTFB',
        value: ttfb,
        rating: getRating('TTFB', ttfb),
        id: generateId(),
        navigationType: navigation.type,
      });
    }
  }, [reportMetric]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    // Check for PerformanceObserver support
    if (!('PerformanceObserver' in window)) {
      if (logToConsole) {
        console.warn('[Web Vitals] PerformanceObserver not supported');
      }
      return;
    }

    const observers: PerformanceObserver[] = [];

    // Observe FCP (First Contentful Paint)
    try {
      const fcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const fcpEntry = entries.find((entry) => entry.name === 'first-contentful-paint');
        if (fcpEntry) {
          reportMetric({
            name: 'FCP',
            value: fcpEntry.startTime,
            rating: getRating('FCP', fcpEntry.startTime),
            id: generateId(),
          });
        }
      });
      fcpObserver.observe({ type: 'paint', buffered: true });
      observers.push(fcpObserver);
    } catch {
      // FCP observation not supported
    }

    // Observe LCP (Largest Contentful Paint)
    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        if (lastEntry) {
          reportMetric({
            name: 'LCP',
            value: lastEntry.startTime,
            rating: getRating('LCP', lastEntry.startTime),
            id: generateId(),
          });
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      observers.push(lcpObserver);
    } catch {
      // LCP observation not supported
    }

    // Observe CLS (Cumulative Layout Shift)
    try {
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          // Only count layout shifts without recent user input
          const layoutShiftEntry = entry as PerformanceEntry & {
            hadRecentInput?: boolean;
            value?: number;
          };
          if (!layoutShiftEntry.hadRecentInput && layoutShiftEntry.value) {
            clsValueRef.current += layoutShiftEntry.value;
            reportMetric({
              name: 'CLS',
              value: clsValueRef.current,
              rating: getRating('CLS', clsValueRef.current),
              delta: layoutShiftEntry.value,
              id: generateId(),
            });
          }
        }
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
      observers.push(clsObserver);
    } catch {
      // CLS observation not supported
    }

    // Observe FID (First Input Delay) - Note: Being replaced by INP
    try {
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const firstEntry = entries[0] as PerformanceEventTiming | undefined;
        if (firstEntry) {
          const fid = firstEntry.processingStart - firstEntry.startTime;
          reportMetric({
            name: 'FID',
            value: fid,
            rating: getRating('FID', fid),
            id: generateId(),
          });
        }
      });
      fidObserver.observe({ type: 'first-input', buffered: true });
      observers.push(fidObserver);
    } catch {
      // FID observation not supported
    }

    observersRef.current = observers;

    // Collect initial metrics (TTFB)
    collectMetrics();

    // Report final LCP on page hide
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Disconnect observers to get final values
        observers.forEach((observer) => {
          try {
            observer.takeRecords();
            observer.disconnect();
          } catch {
            // Ignore errors during cleanup
          }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      observers.forEach((observer) => {
        try {
          observer.disconnect();
        } catch {
          // Ignore errors during cleanup
        }
      });
    };
  }, [enabled, logToConsole, reportMetric, collectMetrics]);

  return {
    metrics: metricsRef.current,
    collectMetrics,
  };
}

export default useWebVitals;
